/**
 * Offline time-limit configuration and lockout math.
 *
 * TODO(API-4643): The limit and "approaching" threshold are currently fixed
 * global constants. API-4643 owns turning these into a real, configured limit
 * (e.g. fetched/cached per company or driven by a product setting). This module
 * is the single seam every offline-limit consumer calls into, so that later
 * work only has to change how `OFFLINE_LIMIT_DAYS` is sourced — not the call
 * sites. The UI (banner countdown + lockout screen, API-4380) and the punch
 * dispatch guard (`offlinePunchService`, API-4650) both read from here.
 */

/** Maximum number of days the terminal may operate offline before lockout. */
export const OFFLINE_LIMIT_DAYS = 7;

/**
 * When the remaining offline time is at or below this many days, the punch-pad
 * banner switches to the time-limit variant ("You can punch offline for X more
 * days…"). Drives the "approaching" state, not the lockout itself.
 */
export const OFFLINE_LIMIT_APPROACHING_DAYS = 2;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface OfflineTimeStatus {
    /** True once the offline limit has been exceeded — punching is locked out. */
    isLockedOut: boolean;
    /**
     * True while offline and within `OFFLINE_LIMIT_APPROACHING_DAYS` of the
     * limit (but not yet locked out). Drives the time-limit banner variant.
     */
    isApproaching: boolean;
    /**
     * Whole days remaining before lockout, rounded up. `null` while online
     * (no `offlineSince`). `0` once locked out.
     */
    remainingDays: number | null;
}

/**
 * Derives the offline time-limit status from when the terminal first went
 * offline. `now` is injectable for testing.
 */
export function getOfflineTimeStatus(
    offlineSince: Date | null,
    now: Date = new Date()
): OfflineTimeStatus {
    if (!offlineSince) {
        return { isLockedOut: false, isApproaching: false, remainingDays: null };
    }

    const remainingMs =
        OFFLINE_LIMIT_DAYS * MS_PER_DAY -
        (now.getTime() - offlineSince.getTime());

    if (remainingMs <= 0) {
        return { isLockedOut: true, isApproaching: false, remainingDays: 0 };
    }

    const remainingDays = Math.ceil(remainingMs / MS_PER_DAY);
    return {
        isLockedOut: false,
        isApproaching: remainingDays <= OFFLINE_LIMIT_APPROACHING_DAYS,
        remainingDays
    };
}

/**
 * Whether offline punching is currently locked out by the elapsed-time limit.
 * Thin wrapper over {@link getOfflineTimeStatus} kept as the stable entry point
 * for the punch dispatch guard.
 */
export function isOfflineLimitExceeded(
    offlineSince: Date | null,
    now: Date = new Date()
): boolean {
    return getOfflineTimeStatus(offlineSince, now).isLockedOut;
}
